import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { BetOdd } from 'components/BetOdd/BetOdd';
import { CountryFlag, TeamCrest } from 'components/TeamCrest/TeamCrest';
import { getAllBets } from 'helpers/matchHelper';
import { translateBetMarketName, translateOddText } from 'helpers/betI18n';
import { formatDateTime } from 'helpers/dateHelper';
import type { MatchOffer } from 'types/MatchOffer';
import { MatchInsights } from 'components/MatchInsights/MatchInsights';
import { describeMarket } from 'helpers/marketDescriptions';
import { groupMarkets, MarketGroup } from 'helpers/marketGroups';
import { useOfferSections } from 'helpers/offerLayout';
import {
  overUnderLines,
  pickLabelText,
  splitMarkets,
  type MarketCard,
} from 'helpers/marketLines';
import { filterMarkets } from 'helpers/marketSearch';
import {
  MarketSearch,
  MarketSearchEmpty,
  MarketSearchToggle,
} from 'components/MarketSearch/MarketSearch';
import styles from './ModernMatchDetails.module.css';

/** Modern layout match page: crests and kick-off on top, then tabs: the
 *  markets (each line of a market its own card of picks), the match
 *  statistics and head to head. The markets are in the same sections as the
 *  classic layout's, one under the other, opened and closed by their names
 *  (owner, 2026-10-30: "isto i tamo i vamo"); the sections and their order
 *  are the admin's offer layout. */
export const ModernMatchDetails = ({ match }: { match: MatchOffer }) => {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState<'markets' | 'statistics' | 'h2h'>('markets');
  const [explained, setExplained] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const cards = useMemo(
    () =>
      splitMarkets(
        getAllBets(match, true).filter((b) => b && b.odds?.length),
        { home: match.home?.name, away: match.away?.name }
      ),
    [match]
  );
  const layout = useOfferSections(match.league?.sport, 'prematch');
  const searching = query.trim() !== '';
  const found = searching ? filterMarkets(cards, query, t) : cards;
  const sections = groupMarkets(found, layout);
  // Until the player opens or closes one, the first section is open - also
  // when the layout arrives after the first render. While searching every
  // section with a match is open.
  const [picked, setPicked] = useState<Set<MarketGroup> | null>(null);
  const open =
    picked ??
    new Set(
      groupMarkets(cards, layout)
        .slice(0, 1)
        .map((x) => x.group)
    );
  const isOpen = (g: MarketGroup) => searching || open.has(g);
  const allOpen = sections.every((x) => isOpen(x.group));
  const toggle = (g: MarketGroup) => {
    const next = new Set(open);
    if (!next.delete(g)) next.add(g);
    setPicked(next);
  };
  const shownCount = found.length;

  const renderCard = (b: MarketCard, id: string) => {
    const key = `${b.name}-${id}`;
    const description = describeMarket(b.name, i18n.language);
    return (
      <section key={key} className={styles.market}>
        <h3 className={styles.marketName}>
          {translateBetMarketName(b, t)}
          {b.part && ` – ${t(`lines_part_${b.part}`)}`}
          {b.line && ` ${b.line}`}
          {description && (
            <button
              type="button"
              className={styles.info}
              aria-label={t('market_description_toggle')}
              aria-expanded={explained === key}
              onClick={() => setExplained(explained === key ? null : key)}
            >
              i
            </button>
          )}
        </h3>
        {description && explained === key && (
          <p className={styles.description}>{description}</p>
        )}
        <div
          className={styles.odds}
          data-cols={
            b.odds!.length % 3 === 0 && !overUnderLines(b.odds!) ? 3 : 2
          }
        >
          {b.odds!.map((o, j) => {
            const short = b.pickLabels?.[o.custom_id];
            return (
              <BetOdd
                key={j}
                odd={o}
                bet={b}
                match={match}
                label={
                  short
                    ? pickLabelText(short, (w) => translateOddText(w, t))
                    : undefined
                }
              />
            );
          })}
        </div>
      </section>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.hero}>
        <div className={styles.league}>
          <CountryFlag cc={match.league.cc} className={styles.flag} />
          <span>{match.league.name}</span>
        </div>
        <div className={styles.teams}>
          <div className={styles.team}>
            <TeamCrest
              imageId={match.home?.image_id}
              size="m"
              className={styles.crest}
            />
            <span>{match.home?.name}</span>
          </div>
          <div className={styles.kickoff}>
            {formatDateTime(new Date(match.time * 1000), {
              removeSeconds: true,
              removeYear: true,
            })}
          </div>
          <div className={styles.team}>
            <TeamCrest
              imageId={match.away?.image_id}
              size="m"
              className={styles.crest}
            />
            <span>{match.away?.name}</span>
          </div>
        </div>
      </div>

      <div className={styles.views} role="tablist">
        {(['markets', 'statistics', 'h2h'] as const).map((v) => (
          <button
            type="button"
            key={v}
            role="tab"
            aria-selected={view === v}
            className={classNames(styles.view, view === v && styles.viewOn)}
            onClick={() => setView(v)}
          >
            {
              {
                markets: t('modern_tab_markets'),
                statistics: t('match_statistics_label'),
                h2h: t('match_h2h'),
              }[v]
            }
          </button>
        ))}
      </div>

      {view === 'statistics' && (
        <div className={styles.statistics}>
          <MatchInsights fi={match.fi} hideScoreboard h2h="exclude" />
        </div>
      )}

      {view === 'h2h' && (
        <div className={styles.statistics}>
          <MatchInsights fi={match.fi} hideScoreboard h2h="only" />
        </div>
      )}

      {/* The magnifier sits ahead of "Open all"; tapped, the field takes
          the toolbar's place until it is closed. */}
      {view === 'markets' && searchOpen && (
        <MarketSearch
          value={query}
          onChange={setQuery}
          found={shownCount}
          onClose={() => setSearchOpen(false)}
        />
      )}

      {view === 'markets' && searching && !shownCount && (
        <MarketSearchEmpty query={query} />
      )}

      {view === 'markets' && !searchOpen && sections.length > 1 && (
        <div className={styles.toolbar}>
          {cards.length > 8 && (
            <MarketSearchToggle onClick={() => setSearchOpen(true)} />
          )}
          <button
            type="button"
            className={styles.toggleAll}
            onClick={() =>
              setPicked(
                allOpen ? new Set() : new Set(sections.map((x) => x.group))
              )
            }
          >
            {allOpen
              ? t('market_groups_collapse_all')
              : t('market_groups_expand_all')}
          </button>
        </div>
      )}

      {view === 'markets' &&
        (sections.length > 1
          ? sections.map(({ group: g, bets }) => (
              <section key={g} className={styles.group}>
                <button
                  type="button"
                  className={classNames(
                    styles.groupHead,
                    isOpen(g) && styles.groupHeadOpen
                  )}
                  aria-expanded={isOpen(g)}
                  onClick={() => toggle(g)}
                >
                  <span className={styles.chevron} aria-hidden="true" />
                  <span>{t(`market_group_${g}`)}</span>
                  <span className={styles.groupCount}>{bets.length}</span>
                </button>
                {isOpen(g) && (
                  <div className={styles.groupBody}>
                    {bets.map((b, i) => renderCard(b, `${g}-${i}`))}
                  </div>
                )}
              </section>
            ))
          : found.map((b, i) => renderCard(b, String(i))))}
    </div>
  );
};
